// Plain words for rules, what-ifs and calculation results (Lane 1344, Phase 3).
// The server decides every figure; these only name things for the buyer.
import { formatAmount } from './sourcingApi';

export const RULE_TYPES = [
  ['keep', 'Keep lines with a supplier'],
  ['keep_no_change', 'Keep lines as today'],
  ['exclude', 'Leave a supplier out'],
  ['approved_only', 'Approved suppliers only'],
  ['one_supplier', 'One supplier for a group'],
  ['all_or_nothing', 'A supplier gets all of a group or none'],
  ['split', 'Split lines between suppliers'],
  ['max_suppliers', 'At most N suppliers'],
  ['min_sources', 'At least N suppliers per line'],
  ['threshold', 'Move a line only for a real saving'],
  ['supplier_limits', 'Limits for one supplier'],
  ['lead_time_limit', 'Longest lead time'],
  ['qualification_cost', 'What qualifying costs'],
  ['prefer_fewer_suppliers', 'Prefer fewer suppliers'],
  ['prefer_fewer_moves', "Prefer today's supplier"],
  ['not_modeled', 'A need Wingman cannot calculate'],
];

export const STATE_TEXT = { proposed: 'Proposed', confirmed: 'Confirmed', rejected: 'Rejected' };
export const SOURCE_TEXT = { manual: 'Made by hand', chat_turn: 'From the chat', buyer_note: 'From a note',
  default: 'Wingman default' };

// The category name a buyer reads (the server's `label` in the filter options: the deck's
// one display rule, "O-rings" not "Oring"; Lane 1382). A filter keeps the raw value; a
// value the options do not name is shown as it is.
export function categoryLabeler(options) {
  const labels = new Map();
  ((options && options.categories) || []).forEach((c) => {
    if (c.label) labels.set(c.level_1, c.label);
    (c.level_2 || []).forEach((s) => { if (s.label) labels.set(s.level_2, s.label); });
  });
  return (raw) => labels.get(raw) || raw;
}

const asIs = (raw) => raw;

export function supplierName(names, id) {
  if (id === null || id === undefined) return 'a supplier';
  const key = String(id).replace(/^s/, '');
  return names[key] || `supplier ${key}`;
}

// Every filter a selection applies (the server's sourcing_scenario_views.scope_text says
// the same), so a rule never reads wider than what it selects.
export function scopeText(selection, names, labelOf = asIs) {
  if (!selection) return 'every line';
  if (selection.line_uids) return `${selection.line_uids.length} chosen ${selection.line_uids.length === 1 ? 'line' : 'lines'}`;
  const f = selection.filter || {};
  const set = (v) => v !== undefined && v !== null && v !== '';
  if (!['level_1', 'level_2', 'site', 'search', 'incumbent_supplier_id', 'included', 'incomplete'].some((k) => set(f[k]))) return 'every line';
  let text = f.included === false ? 'excluded lines' : f.included === true ? 'included lines' : 'lines';
  const cats = [f.level_1, f.level_2].filter(Boolean).map(labelOf);
  if (cats.length) text += ` in ${cats.join(' / ')}`;
  if (f.site) text += ` at ${f.site}`;
  if (f.search) text += ` with parts matching "${f.search}"`;
  if (set(f.incumbent_supplier_id)) text += ` bought today from ${supplierName(names || {}, f.incumbent_supplier_id)}`;
  if (f.incomplete === true) text += ' that need attention';
  else if (f.incomplete === false) text += ' that are complete';
  return text;
}

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

// What a result's total covers, in one line (used by cards and the result page).
export function coverageText(totals) {
  const cov = (totals && totals.coverage) || {};
  const n = (k) => (cov[k] ? cov[k].lines : 0);
  const parts = [`${plural(n('offers'), 'line', 'lines')} to offers`];
  if (n('no_change')) parts.push(`${n('no_change')} kept as today`);
  if (n('unallocatable')) parts.push(`${n('unallocatable')} with no option`);
  const notCalc = totals && totals.not_calculated ? totals.not_calculated.lines : 0;
  if (notCalc) parts.push(`${notCalc} not calculated`);
  return parts.join(', ');
}

export function ruleText(rule, names, currency, labelOf = asIs) {
  const p = rule.params || {};
  const who = supplierName(names, p.supplier_id);
  const scope = scopeText(rule.selection, names, labelOf);
  const every = scope === 'every line';
  const one = scope === '1 chosen line';
  const atLeast = `At least ${plural(p.n, 'supplier', 'suppliers')}`;
  switch (rule.rule_type) {
    case 'keep': return `Keep ${scope} with ${who}`;
    case 'keep_no_change': return `Keep ${scope} as today`;
    case 'exclude': return `Leave ${who} out of ${scope}`;
    case 'approved_only': return `Only approved suppliers on ${scope}`;
    case 'one_supplier': return `One supplier for ${scope}${p.allow_no_change ? " (today's supplier may keep it as today)" : ''}`;
    case 'all_or_nothing': return every ? `${who} gets every line or none of them` : `${who} gets all of ${scope} or none of it`;
    case 'split': return `Split ${scope}: ${Object.entries(p.shares || {}).map(([s, n]) => `${supplierName(names, s)} ${n}`).join(' / ')}`;
    case 'max_suppliers': return `At most ${p.n} ${p.n === 1 ? 'supplier' : 'suppliers'}${rule.selection ? ` on ${scope}` : ' in the event'}`;
    // Never "on each of every line", never "at least 0%" (live staging, 2026-10-03).
    case 'min_sources': return `${atLeast} ${every ? 'on every line' : one ? `on ${scope}` : `on each of ${scope}`}${(p.share_n ?? 1) === 0 ? ', with no minimum share each' : `, each at least ${Math.round((100 * (p.share_n ?? 1)) / (p.share_d ?? 5))}%`}`;
    case 'threshold': return thresholdText(p, scope, currency);
    case 'supplier_limits': return supplierLimitsText(p, who, every ? '' : ` on ${scope}`, currency);
    case 'lead_time_limit': return `Only offers with a lead time of ${plural(Number(p.days), 'day', 'days')} or less can win ${scope}`;
    case 'qualification_cost': return `Qualifying costs ${dollars(p.amount, currency)} for ${p.per === 'supplier' ? 'each new supplier' : 'each part a supplier not yet approved for it wins'}${every ? '' : ` (${scope})`}${Number(p.years) > 1 ? `, spread over ${p.years} years` : ''}`;
    case 'prefer_fewer_suppliers': return `Prefer fewer suppliers (${formatAmount(p.amount, currency)} for each extra supplier)`;
    case 'prefer_fewer_moves': return `Prefer today's supplier (${formatAmount(p.amount, currency, 4)} for each unit moved)`;
    case 'not_modeled': return `Not in the calculation: ${rule.description}`;
    case 'keep_as_today_allowed': return `A part can stay with today's supplier at today's price${rule.selection ? ` (${scope})` : ''}`;
    case 'unapproved_allowed': return `A supplier not yet approved for a part can win it${rule.selection ? ` (${scope})` : ''}`;
    default: return rule.rule_type;
  }
}

// A buyer's amount: whole when it is whole ($1,000), else with cents (Lane 1395; the
// server's sourcing_scenario_views.dollars says the same).
function dollars(v, currency) {
  const whole = Number(v) === Math.round(Number(v));
  return formatAmount(v, currency, whole ? 0 : 2);
}

function thresholdText(p, scope, currency) {
  const tests = [];
  if (p.percent !== undefined && p.percent !== null) tests.push(`it saves more than ${p.percent}%`);
  if (p.amount !== undefined && p.amount !== null) {
    tests.push(`it saves at least ${dollars(p.amount, currency)} a year ${p.per === 'site' ? 'on each site' : 'on the part, all sites together'}`);
  }
  return `Move ${scope} away from today's supplier only if ${tests.join(' and ')}`;
}

function supplierLimitsText(p, who, on, currency) {
  const out = [];
  const hasMax = p.max_amount !== undefined && p.max_amount !== null;
  const hasMin = p.min_amount !== undefined && p.min_amount !== null;
  if (hasMax && hasMin) out.push(`${who} gets between ${dollars(p.min_amount, currency)} and ${dollars(p.max_amount, currency)} a year${on}`);
  else if (hasMax) out.push(`${who} gets at most ${dollars(p.max_amount, currency)} a year${on}`);
  else if (hasMin) out.push(`${who} gets at least ${dollars(p.min_amount, currency)} a year${on}`);
  if (p.no_new_parts) out.push(`${who} gets no new parts${on}`);
  if (p.risk_percent) out.push(`${who}'s prices count ${p.risk_percent}% higher for risk${on} when choosing`);
  return out.join('. ');
}

export function whatIfText(w, names, currency, labelOf = asIs) {
  const scope = scopeText(w.selection, names, labelOf);
  const who = supplierName(names, w.supplier_id);
  switch (w.kind) {
    case 'assign': return `Give ${scope} to ${who}`;
    case 'keep_as_today': return `Keep ${scope} as today`;
    case 'split': return `Split ${scope}: ${Object.entries(w.shares || {}).map(([s, n]) => `${supplierName(names, s)} ${n}`).join(' / ')}`;
    case 'incumbents_keep': return `Today's suppliers keep ${scope}`;
    case 'assign_quoted': return scope === 'every line' ? `${who} takes every part it quoted`
      : scope === '1 chosen line' ? `${who} takes the chosen line if it quoted it`
        : `${who} takes every part it quoted in ${scope}`;
    case 'split_with_incumbent': return `Where both quoted, split ${scope} ${w.share_incumbent}:${w.share_supplier} between today's supplier and ${who}`;
    case 'leave_out': return `Leave ${who} out of ${scope}`;
    case 'incumbent_or_approved': return `On ${scope}, only today's supplier or an approved supplier`;
    case 'price': return `${who} at your prices on ${Object.keys(w.prices || {}).length} parts`;
    default: return w.kind;
  }
}

export const MODE_TEXT = {
  strict: 'Approved suppliers only',
  exploring: 'Exploring (unapproved suppliers allowed, on condition of qualification)',
};

export const RESULT_STATUS_TEXT = {
  optimal: 'Best answer found',
  feasible: 'Good answer, not proven best (the time limit was reached)',
  infeasible: 'The rules cannot all hold',
  unknown: 'No answer in the time limit',
  failed: 'The calculation failed',
};

export const NOT_CALCULATED_TEXT = {
  missing_quantity: 'no estimated annual volume',
  missing_unit: 'no unit of measure',
  part_units_differ: "the part's sites use different units",
  quantity_not_whole_steps: 'the volume is not a whole number of buying steps',
};

export function jobText(job) {
  if (!job) return '';
  if (job.status === 'failed') return 'The calculation did not finish.';
  if (job.status === 'queued' && job.waiting) return 'Waiting for the calculation service.';
  if (job.status === 'queued' || job.status === 'running') return 'Calculating…';
  return '';
}
